import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import type {
  ReceptionModificationRequest,
  RequestReceptionModificationInput,
} from "~/types/receptionModification";

// reception_modification_requests + its RPCs postdate the generated Supabase
// types, so they are reached through an untyped client handle (same approach the
// codebase uses for other post-generation tables). Results cast to the domain type.
const db = supabase as unknown as {
  rpc: (
    fn: string,
    args: Record<string, unknown>
  ) => Promise<{ data: any; error: any }>;
};

const QUERY_KEY = "reception-modification-requests";

// Queue of reception edit/delete requests. Requesters (any Órdenes de Compra
// role) submit from the PO detail; approvers (superadmin/admin) resolve — the
// reception/stock is never touched until a request is approved.
export const useReceptionModificationRequests = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await db.rpc(
        "get_reception_modification_requests",
        { p_status: null }
      );
      if (error) throw error;
      return (data ?? []) as ReceptionModificationRequest[];
    },
  });

  // Approval mutates stock, PO received quantities and receptions — refresh all.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: ["purchase_orders"] });
    queryClient.invalidateQueries({ queryKey: ["purchase_order_receptions"] });
    queryClient.invalidateQueries({ queryKey: ["oil_types"] });
    queryClient.invalidateQueries({ queryKey: ["raw_materials"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  const request = useMutation({
    mutationFn: async (input: RequestReceptionModificationInput) => {
      const { error } = await db.rpc("request_reception_modification", {
        p_reception_item_id: input.receptionItemId,
        p_request_type: input.requestType,
        p_reason: input.reason,
        p_new_quantity: input.newQuantity ?? null,
        p_new_unit_cost: input.newUnitCost ?? null,
        p_new_expiry_date: input.newExpiryDate ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Solicitud enviada",
        description: "La modificación quedó pendiente de autorización.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo enviar la solicitud.",
        variant: "destructive",
      });
    },
  });

  const approve = useMutation({
    mutationFn: async ({
      requestId,
      notes,
    }: {
      requestId: string;
      notes?: string;
    }) => {
      const { error } = await db.rpc("approve_reception_modification", {
        p_request_id: requestId,
        p_review_notes: notes ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Modificación aplicada",
        description: "El cambio fue ejecutado sobre la recepción y el stock.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo aprobar la solicitud.",
        variant: "destructive",
      });
    },
  });

  const reject = useMutation({
    mutationFn: async ({
      requestId,
      notes,
    }: {
      requestId: string;
      notes: string;
    }) => {
      const { error } = await db.rpc("reject_reception_modification", {
        p_request_id: requestId,
        p_review_notes: notes,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Solicitud rechazada",
        description: "La solicitud fue marcada como rechazada.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo rechazar la solicitud.",
        variant: "destructive",
      });
    },
  });

  return {
    requests,
    isLoading,
    request: request.mutateAsync,
    approve: approve.mutateAsync,
    reject: reject.mutateAsync,
    isRequesting: request.isPending,
    isApproving: approve.isPending,
    isRejecting: reject.isPending,
    pendingCount: requests.filter((r) => r.status === "pending").length,
  };
};
